/* ─────────────────────────────────────────────────────────────────────────────
   D2C Freight Pricing — RTK Query slice (the single data layer for the module).

   Transport: the shared axios helpers (get/post/putGeneric/postMultipart) from
   constants.js, which prepend ENDPOINT (the local 8099 /api/ base). D2C endpoints
   live under that same base as `d2c/...` (i.e. /api/d2c/...), so no separate host
   or custom baseQuery is needed — the global auth + 401-refresh interceptors apply.

   Endpoint PATHS come from apiConstants (constants.js), like the rest of the app.
   D2C responses are RAW JSON (no GenericAPIResponse envelope) — we return res.data
   as-is; there is NO unwrap().
   ──────────────────────────────────────────────────────────────────────────── */
import { createApi } from '@reduxjs/toolkit/query/react'
import {
  apiConstants,
  get,
  post,
  putGeneric,
  postMultipart,
  deleteGeneric,
} from '../common/constants'

const PROPOSALS = apiConstants.D2C_PROPOSALS            // 'd2c/proposals'
const VERSIONS = apiConstants.D2C_PROPOSAL_VERSIONS     // 'd2c/proposal-versions'
const CONFIG = apiConstants.D2C_CONFIG                  // 'd2c/config'
const ADMIN_PROPOSALS = apiConstants.D2C_ADMIN_PROPOSALS
const PINCODE_IMPORT = apiConstants.D2C_ADMIN_PINCODE_IMPORT
const PINCODES = apiConstants.D2C_MASTERS_PINCODES
const ZONE = apiConstants.D2C_MASTERS_ZONE
const COURIERS = apiConstants.D2C_MASTERS_COURIERS
// v2 masters + collaboration endpoints (FE Integration Guide v2). Inline to keep contained.
const RESTRICTED = 'd2c/masters/restricted-couriers'
const SUPPLY_COMMERCIALS = 'd2c/masters/supply-commercials'
const DROP_SERVICEABILITY = 'd2c/masters/drop-serviceability'
const CP_SERVICE_MAPPING = 'd2c/masters/cp-service-mapping'
const CP_CAPABILITY = 'd2c/masters/cp-capability'
const TOOLKIT = 'd2c/toolkit'
const RATE_CARD_LIBRARY = 'd2c/masters/rate-card-library'
const RATE_CARD_UPLOAD = 'd2c/masters/rate-card-library/upload'
const STANDARD_CARDS = 'd2c/masters/standard-rate-cards'
const USERS = 'd2c/users'

/* axios call → RTK Query {data} | {error}. Returns the raw response body (res.data). */
const run = async (promise) => {
  try {
    const res = await promise
    return { data: res?.data }
  } catch (err) {
    const r = err?.response
    return {
      error: {
        status: r?.status,
        data: r?.data, // D2C errors: { code, message } or { error, reason }
        message: r?.data?.message || r?.data?.reason || err.message,
      },
    }
  }
}

export const d2cApiSlice = createApi({
  reducerPath: 'd2cApi',
  baseQuery: () => ({ data: undefined }), // unused — every endpoint defines its own queryFn
  tagTypes: ['Proposal', 'Versions', 'Version', 'Config', 'Comments', 'Restricted', 'Toolkit', 'Library', 'CpCapability', 'StandardCards', 'Doc'],
  endpoints: (b) => ({
    // ── Proposal lifecycle (SL) ────────────────────────────────────────────
    createProposal: b.mutation({
      queryFn: (body) => run(post(PROPOSALS, body)),
      invalidatesTags: ['Proposal'],
    }),
    // PROD-BUG-02: full deal detail (incl. draftState) for Resume, + autosave of the wizard snapshot.
    getProposal: b.query({
      queryFn: (proposalId) => run(get(`${PROPOSALS}/${proposalId}`)),
      providesTags: ['Proposal'],
    }),
    saveDraftState: b.mutation({
      queryFn: ({ proposalId, draftState }) => run(putGeneric(`${PROPOSALS}/${proposalId}/draft-state`, { draftState })),
    }),
    generate: b.mutation({
      queryFn: ({ proposalId, body }) => run(post(`${PROPOSALS}/${proposalId}/generate`, body)),
      invalidatesTags: ['Proposal', 'Versions'],
    }),
    submit: b.mutation({
      queryFn: (proposalId) => run(post(`${PROPOSALS}/${proposalId}/submit`)),
      invalidatesTags: ['Proposal', 'Versions'],
    }),
    // QA #8: POST /accept-counter sends the countered version back to the reviewer
    // unchanged (no engine run, no new seq). DELIBERATELY UNUSED for now — per the
    // Aug-2026 product decision, "accepting" a counter is NOT an auto-send: the SL
    // reviews the countered numbers on SLCounterReview and resubmits (generate+submit)
    // as the only forward action. Kept wired for the planned enhancement that flips this.
    acceptCounter: b.mutation({
      queryFn: (proposalId) => run(post(`${PROPOSALS}/${proposalId}/accept-counter`)),
      invalidatesTags: ['Proposal', 'Versions'],
    }),
    push: b.mutation({
      queryFn: (proposalId) => run(post(`${PROPOSALS}/${proposalId}/push`)),
      invalidatesTags: ['Proposal', 'Versions'],
    }),
    // v1.2: multipart CSV upload (field name "file"). Moves PUSHED → PENDING_L1, no re-pricing.
    uploadOrderData: b.mutation({
      queryFn: ({ proposalId, file }) => {
        const fd = new FormData()
        fd.append('file', file)
        return run(postMultipart(`${PROPOSALS}/${proposalId}/upload-order-data`, fd))
      },
      invalidatesTags: ['Proposal', 'Versions'],
    }),
    // v1.2: blank CSV template download (returns a Blob).
    getOrderDataTemplate: b.query({
      queryFn: () => run(get(`${PROPOSALS}/order-data-template`, {}, { responseType: 'blob' })),
    }),
    send: b.mutation({
      queryFn: (proposalId) => run(post(`${PROPOSALS}/${proposalId}/send`)),
      // 'Doc' too — send is what generates the client PDF, so the cached "not
      // generated yet" getProposalDoc result must refetch immediately (users had
      // to hard-refresh the browser to see the fresh PDF link).
      invalidatesTags: (r, e, proposalId) => ['Proposal', { type: 'Doc', id: proposalId }],
    }),
    outcome: b.mutation({
      queryFn: ({ proposalId, body }) => run(post(`${PROPOSALS}/${proposalId}/outcome`, body)),
      invalidatesTags: ['Proposal'],
    }),

    // ── Shared reads ───────────────────────────────────────────────────────
    listProposals: b.query({
      queryFn: ({ page = 0, size = 20 } = {}) => run(get(PROPOSALS, { page, size })),
      providesTags: ['Proposal'],
    }),
    getVersions: b.query({
      queryFn: (proposalId) => run(get(`${PROPOSALS}/${proposalId}/versions`)),
      providesTags: ['Versions'],
    }),
    getVersion: b.query({
      queryFn: (versionId) => run(get(`${VERSIONS}/${versionId}`)),
      providesTags: ['Version'],
    }),
    getProposalDoc: b.query({
      queryFn: (proposalId) => run(get(`${PROPOSALS}/${proposalId}/proposal-doc`)),
      providesTags: (r, e, proposalId) => [{ type: 'Doc', id: proposalId }],
    }),
    getPincode: b.query({
      queryFn: (pin) => run(get(`${PINCODES}/${pin}`)),
    }),
    getZone: b.query({
      queryFn: ({ pickupPin, dropPin }) => run(get(ZONE, { pickupPin, dropPin })),
    }),
    // FE Integration Guide §22 — D2C courier master (returns cpAccountCode for cpPreference).
    getCouriers: b.query({
      queryFn: (arg) => run(get(COURIERS, arg?.serviceType ? { serviceType: arg.serviceType } : {})),
    }),

    // ── System margin & FSC (SL) ───────────────────────────────────────────
    systemMargin: b.mutation({
      queryFn: ({ versionId, body }) => run(post(`${VERSIONS}/${versionId}/system-margin`, body)),
      invalidatesTags: ['Proposal', 'Versions'],
    }),
    fsc: b.mutation({
      queryFn: ({ versionId, body }) => run(post(`${VERSIONS}/${versionId}/fsc`, body)),
      invalidatesTags: ['Proposal', 'Versions'],
    }),

    // ── Decision actions (L1 / FH) ─────────────────────────────────────────
    approve: b.mutation({
      queryFn: (versionId) => run(post(`${VERSIONS}/${versionId}/approve`)),
      invalidatesTags: ['Proposal', 'Versions', 'Version'],
    }),
    escalate: b.mutation({
      queryFn: ({ versionId, body }) => run(post(`${VERSIONS}/${versionId}/escalate`, body)),
      invalidatesTags: ['Proposal', 'Versions', 'Version'],
    }),
    returnProposal: b.mutation({
      queryFn: ({ versionId, body }) => run(post(`${VERSIONS}/${versionId}/return`, body)),
      invalidatesTags: ['Proposal', 'Versions', 'Version'],
    }),
    decline: b.mutation({
      queryFn: ({ versionId, body }) => run(post(`${VERSIONS}/${versionId}/decline`, body)),
      invalidatesTags: ['Proposal', 'Versions', 'Version'],
    }),
    counter: b.mutation({
      queryFn: ({ versionId, body }) => run(post(`${VERSIONS}/${versionId}/counter`, body)),
      invalidatesTags: ['Proposal', 'Versions', 'Version'],
    }),

    // ── Admin (FH) ─────────────────────────────────────────────────────────
    getConfig: b.query({
      queryFn: () => run(get(CONFIG)),
      providesTags: ['Config'],
    }),
    updateConfig: b.mutation({
      queryFn: (body) => run(putGeneric(CONFIG, body)),
      invalidatesTags: ['Config'],
    }),
    transferOwnership: b.mutation({
      queryFn: ({ proposalId, body }) => run(putGeneric(`${ADMIN_PROPOSALS}/${proposalId}/transfer`, body)),
      invalidatesTags: ['Proposal'],
    }),
    listAllProposals: b.query({
      queryFn: ({ page = 0, size = 20 } = {}) => run(get(ADMIN_PROPOSALS, { page, size })),
      providesTags: ['Proposal'],
    }),
    importPincodes: b.mutation({
      queryFn: (formData) => run(postMultipart(PINCODE_IMPORT, formData)),
    }),

    // ── v2 §1 Comments & attachments (any D2C role on the deal) ──────────────
    getComments: b.query({
      queryFn: (proposalId) => run(get(`${PROPOSALS}/${proposalId}/comments`)),
      providesTags: (r, e, id) => [{ type: 'Comments', id }],
    }),
    postComment: b.mutation({
      queryFn: ({ proposalId, text, kind = 'comment' }) => run(post(`${PROPOSALS}/${proposalId}/comments`, { text, kind })),
      invalidatesTags: (r, e, { proposalId }) => [{ type: 'Comments', id: proposalId }],
    }),
    postCommentAttachment: b.mutation({
      queryFn: ({ proposalId, commentId, formData }) => run(postMultipart(`${PROPOSALS}/${proposalId}/comments/${commentId}/attachments`, formData)),
      invalidatesTags: (r, e, { proposalId }) => [{ type: 'Comments', id: proposalId }],
    }),

    // ── v2 §2 Restricted couriers (config-backed; drives the engine) ─────────
    getRestrictedCouriers: b.query({
      queryFn: () => run(get(RESTRICTED)),
      providesTags: ['Restricted'],
    }),
    addRestrictedCourier: b.mutation({
      queryFn: (cpAccountCode) => run(post(RESTRICTED, { cpAccountCode })),
      invalidatesTags: ['Restricted'],
    }),
    removeRestrictedCourier: b.mutation({
      queryFn: (cpAccountCode) => run(deleteGeneric(`${RESTRICTED}/${cpAccountCode}`)),
      invalidatesTags: ['Restricted'],
    }),

    // ── v2 §3-6 Supply masters (FH) ─────────────────────────────────────────
    getSupplyCommercials: b.query({
      queryFn: ({ serviceType } = {}) => run(get(SUPPLY_COMMERCIALS, serviceType ? { serviceType } : {})),
    }),
    getDropServiceability: b.query({
      queryFn: ({ page = 0, size = 200 } = {}) => run(get(DROP_SERVICEABILITY, { page, size })),
    }),
    importDropServiceability: b.mutation({
      queryFn: (formData) => run(postMultipart(`${DROP_SERVICEABILITY}/import`, formData)),
    }),
    getCpServiceMapping: b.query({
      queryFn: ({ serviceType } = {}) => run(get(CP_SERVICE_MAPPING, serviceType ? { serviceType } : {})),
    }),
    getCpCapability: b.query({
      queryFn: () => run(get(CP_CAPABILITY)),
      providesTags: ['CpCapability'],
    }),
    updateCpCapability: b.mutation({
      queryFn: ({ courierId, body }) => run(putGeneric(`${CP_CAPABILITY}/${courierId}`, body)),
      invalidatesTags: ['CpCapability'],
    }),

    // ── v2 §8 GTM Toolkit ───────────────────────────────────────────────────
    getToolkit: b.query({
      queryFn: () => run(get(TOOLKIT)),
      providesTags: ['Toolkit'],
    }),
    addToolkit: b.mutation({
      queryFn: (body) => run(post(TOOLKIT, body)),
      invalidatesTags: ['Toolkit'],
    }),
    updateToolkit: b.mutation({
      queryFn: ({ id, body }) => run(putGeneric(`${TOOLKIT}/${id}`, body)),
      invalidatesTags: ['Toolkit'],
    }),
    deleteToolkit: b.mutation({
      queryFn: (id) => run(deleteGeneric(`${TOOLKIT}/${id}`)),
      invalidatesTags: ['Toolkit'],
    }),

    // ── v2 §9 Rate-card library (FH) ────────────────────────────────────────
    getRateCardLibrary: b.query({
      queryFn: () => run(get(RATE_CARD_LIBRARY)),
      providesTags: ['Library'],
    }),
    addRateCardLibrary: b.mutation({
      queryFn: (body) => run(post(RATE_CARD_LIBRARY, body)),
      invalidatesTags: ['Library'],
    }),
    updateRateCardLibrary: b.mutation({
      queryFn: ({ id, body }) => run(putGeneric(`${RATE_CARD_LIBRARY}/${id}`, body)),
      invalidatesTags: ['Library'],
    }),
    deleteRateCardLibrary: b.mutation({
      queryFn: (id) => run(deleteGeneric(`${RATE_CARD_LIBRARY}/${id}`)),
      invalidatesTags: ['Library'],
    }),
    uploadRateCardLibrary: b.mutation({
      queryFn: (formData) => run(postMultipart(RATE_CARD_UPLOAD, formData)),
    }),

    // ── STANDARD mode — master cards (read) + serve/re-download on a deal ─────
    // Serve moves the deal DRAFT→SENT in one call (no engine, no version); it returns
    // presigned download URLs (forward + auto-attached RVP), or aboveCeiling for >45k.
    listStandardCards: b.query({
      queryFn: () => run(get(STANDARD_CARDS)),
      providesTags: ['StandardCards'],
    }),
    addStandardCard: b.mutation({
      queryFn: (formData) => run(postMultipart(STANDARD_CARDS, formData)),
      invalidatesTags: ['StandardCards'],
    }),
    deleteStandardCard: b.mutation({
      queryFn: (id) => run(deleteGeneric(`${STANDARD_CARDS}/${id}`)),
      invalidatesTags: ['StandardCards'],
    }),
    serveStandardCard: b.mutation({
      queryFn: ({ proposalId, body }) => run(post(`${PROPOSALS}/${proposalId}/standard-card`, body)),
      invalidatesTags: ['Proposal'],
    }),
    getStandardCardDownload: b.query({
      queryFn: (proposalId) => run(get(`${PROPOSALS}/${proposalId}/standard-card`)),
    }),

    // ── v2 §10 Users by D2C role ────────────────────────────────────────────
    getUsers: b.query({
      queryFn: ({ role } = {}) => run(get(USERS, role ? { role } : {})),
    }),

    // Serviceability service-type master (read-only) — powers the dynamic Step-1
    // service picker. Same shared /api base as the serviceability-intel module.
    // The rich values are mapped back to the 4 D2C service enums (apiMap.serviceTypeToKey)
    // before anything is sent to /proposals or /generate — the API contract is unchanged.
    getServiceabilityServiceTypes: b.query({
      queryFn: () => run(get('order/serviceability/intel/service-types', { includeLinkedCodes: true })),
    }),
  }),
})

export const {
  useCreateProposalMutation,
  useGetProposalQuery,
  useLazyGetProposalQuery,
  useSaveDraftStateMutation,
  useGenerateMutation,
  useSubmitMutation,
  useAcceptCounterMutation,
  usePushMutation,
  useUploadOrderDataMutation,
  useLazyGetOrderDataTemplateQuery,
  useSendMutation,
  useOutcomeMutation,
  useListProposalsQuery,
  useGetVersionsQuery,
  useGetVersionQuery,
  useLazyGetVersionQuery,
  useGetProposalDocQuery,
  useGetPincodeQuery,
  useLazyGetPincodeQuery,
  useGetCouriersQuery,
  useGetZoneQuery,
  useLazyGetZoneQuery,
  useSystemMarginMutation,
  useFscMutation,
  useApproveMutation,
  useEscalateMutation,
  useReturnProposalMutation,
  useDeclineMutation,
  useCounterMutation,
  useGetConfigQuery,
  useUpdateConfigMutation,
  useTransferOwnershipMutation,
  useListAllProposalsQuery,
  useImportPincodesMutation,
  useGetCommentsQuery,
  usePostCommentMutation,
  usePostCommentAttachmentMutation,
  useGetRestrictedCouriersQuery,
  useAddRestrictedCourierMutation,
  useRemoveRestrictedCourierMutation,
  useGetSupplyCommercialsQuery,
  useGetDropServiceabilityQuery,
  useImportDropServiceabilityMutation,
  useGetCpServiceMappingQuery,
  useGetCpCapabilityQuery,
  useUpdateCpCapabilityMutation,
  useGetToolkitQuery,
  useAddToolkitMutation,
  useUpdateToolkitMutation,
  useDeleteToolkitMutation,
  useGetRateCardLibraryQuery,
  useAddRateCardLibraryMutation,
  useUpdateRateCardLibraryMutation,
  useDeleteRateCardLibraryMutation,
  useUploadRateCardLibraryMutation,
  useListStandardCardsQuery,
  useAddStandardCardMutation,
  useDeleteStandardCardMutation,
  useServeStandardCardMutation,
  useLazyGetStandardCardDownloadQuery,
  useGetUsersQuery,
  useGetServiceabilityServiceTypesQuery,
} = d2cApiSlice
